/**
 * EmailDetailsBar — barra colapsável no topo do canvas do editor.
 * Substitui o stepper de 4 etapas: Assunto, Preheader, Destinatários, Remetente
 * editáveis inline.
 */

import { useRef, useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Mail, Users, AtSign, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { VariablePicker, insertAtCursor } from "@/components/email/VariablePicker";

export type EmailDetailsBarProps = {
  subject: string;
  onSubjectChange: (v: string) => void;
  preheader: string;
  onPreheaderChange: (v: string) => void;
  segmentIds: number[];
  onSegmentIdsChange: (ids: number[]) => void;
  tagIds: number[];
  onTagIdsChange: (ids: number[]) => void;
  senderName?: string | null;
  senderEmail?: string | null;
  className?: string;
  defaultOpen?: boolean;
};

export function EmailDetailsBar({
  subject,
  onSubjectChange,
  preheader,
  onPreheaderChange,
  segmentIds,
  onSegmentIdsChange,
  tagIds,
  onTagIdsChange,
  senderName,
  senderEmail,
  className,
  defaultOpen = true,
}: EmailDetailsBarProps) {
  const [open, setOpen] = useState(defaultOpen);
  const subjectRef = useRef<HTMLInputElement>(null);
  const preheaderRef = useRef<HTMLInputElement>(null);

  const { data: segments = [] } = trpc.segments.list.useQuery();
  const { data: tags = [] } = trpc.tags.list.useQuery();

  const segmentList = segments as Array<{ id: number; name: string }>;
  const tagList = tags as Array<{ id: number; name: string }>;

  const { data: recipientPreview } = trpc.emailCampaigns.previewRecipients.useQuery(
    { segmentIds, tagIds },
    { enabled: segmentIds.length > 0 || tagIds.length > 0 },
  );
  const recipientCount = recipientPreview?.count ?? 0;
  const withoutEmail = (recipientPreview as { withoutEmail?: number } | undefined)?.withoutEmail ?? 0;

  const recipientLabel = useMemo(() => {
    const total = segmentIds.length + tagIds.length;
    if (total === 0) return "Selecionar destinatários";
    const base = `${total} grupo${total > 1 ? "s" : ""} • ${recipientCount} contato${recipientCount === 1 ? "" : "s"} c/ e-mail`;
    return withoutEmail > 0 ? `${base} (${withoutEmail} sem e-mail)` : base;
  }, [segmentIds.length, tagIds.length, recipientCount, withoutEmail]);

  const toggleSegment = (id: number) => {
    onSegmentIdsChange(segmentIds.includes(id) ? segmentIds.filter((x) => x !== id) : [...segmentIds, id]);
  };
  const toggleTag = (id: number) => {
    onTagIdsChange(tagIds.includes(id) ? tagIds.filter((x) => x !== id) : [...tagIds, id]);
  };

  return (
    <div className={cn("border-b bg-muted/20", className)}>
      {/* Header colapsável: resumo numa linha */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-2 text-xs hover:bg-muted/40 transition-colors text-left"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="truncate text-muted-foreground">
          <span className={subject ? "text-foreground font-medium" : "italic"}>
            {subject || "Sem assunto"}
          </span>
          {recipientCount > 0 && (
            <span className="text-muted-foreground"> • ~{recipientCount} destinatário{recipientCount === 1 ? "" : "s"}</span>
          )}
        </span>
      </button>

      {open && (
        <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-3 border-t bg-background">
          {/* Assunto */}
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Mail className="h-3 w-3" /> Assunto
            </Label>
            <div className="mt-1 flex gap-1">
              <Input
                ref={subjectRef}
                value={subject}
                onChange={(e) => onSubjectChange(e.target.value)}
                placeholder="O que vai aparecer na caixa de entrada"
                className="h-8 text-sm"
              />
              <VariablePicker
                align="end"
                iconOnly
                onInsert={(token) =>
                  insertAtCursor(subjectRef.current, subject, token, onSubjectChange)
                }
              />
            </div>
          </div>

          {/* Preheader */}
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <FileText className="h-3 w-3" /> Preheader
            </Label>
            <div className="mt-1 flex gap-1">
              <Input
                ref={preheaderRef}
                value={preheader}
                onChange={(e) => onPreheaderChange(e.target.value)}
                placeholder="Texto curto que aparece após o assunto"
                className="h-8 text-sm"
              />
              <VariablePicker
                align="end"
                iconOnly
                onInsert={(token) =>
                  insertAtCursor(preheaderRef.current, preheader, token, onPreheaderChange)
                }
              />
            </div>
          </div>

          {/* Destinatários */}
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" /> Destinatários
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="mt-1 h-8 w-full justify-start text-xs font-normal">
                  {recipientLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[calc(100vw-2rem)] sm:w-72 p-0">
                <div className="p-2 border-b">
                  <div className="text-xs font-semibold mb-1">Segmentos</div>
                  <ScrollArea className="h-32">
                    {segmentList.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2">Nenhum segmento.</p>
                    ) : (
                      segmentList.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 px-1 py-1 text-xs cursor-pointer hover:bg-muted rounded">
                          <Checkbox
                            checked={segmentIds.includes(s.id)}
                            onCheckedChange={() => toggleSegment(s.id)}
                          />
                          <span className="flex-1 truncate">{s.name}</span>
                        </label>
                      ))
                    )}
                  </ScrollArea>
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold mb-1">Tags</div>
                  <ScrollArea className="h-32">
                    {tagList.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2">Nenhuma tag.</p>
                    ) : (
                      tagList.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 px-1 py-1 text-xs cursor-pointer hover:bg-muted rounded">
                          <Checkbox
                            checked={tagIds.includes(t.id)}
                            onCheckedChange={() => toggleTag(t.id)}
                          />
                          <span className="flex-1 truncate">{t.name}</span>
                        </label>
                      ))
                    )}
                  </ScrollArea>
                </div>
              </PopoverContent>
            </Popover>
            {(segmentIds.length > 0 || tagIds.length > 0) && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {segmentIds.map((id) => {
                  const s = segmentList.find((x) => x.id === id);
                  return s ? (
                    <Badge key={`s-${id}`} variant="secondary" className="text-[10px] gap-1">
                      {s.name}
                      <button onClick={() => toggleSegment(id)} className="ml-0.5 hover:text-destructive">×</button>
                    </Badge>
                  ) : null;
                })}
                {tagIds.map((id) => {
                  const t = tagList.find((x) => x.id === id);
                  return t ? (
                    <Badge key={`t-${id}`} variant="outline" className="text-[10px] gap-1">
                      {t.name}
                      <button onClick={() => toggleTag(id)} className="ml-0.5 hover:text-destructive">×</button>
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Remetente (somente leitura) */}
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <AtSign className="h-3 w-3" /> Remetente
            </Label>
            <div className="mt-1 h-8 px-2 flex items-center text-xs border rounded-md bg-muted/30">
              {senderName || senderEmail ? (
                <span className="truncate">
                  {senderName ? <span className="font-medium">{senderName}</span> : null}
                  {senderName && senderEmail ? <span className="text-muted-foreground"> &lt;{senderEmail}&gt;</span> : null}
                  {!senderName && senderEmail ? <span>{senderEmail}</span> : null}
                </span>
              ) : (
                <a href="/settings?tab=email" className="text-xs text-primary hover:underline">Configurar remetente</a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
